import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ROUTES } from "../../routes";
import { PaymentType } from "../../features/payment-types/models/PaymentType";

interface PerVisitContractFieldProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** The member already holds an Active per-visit account: shown unticked and disabled. */
  alreadyActive: boolean;
  paymentTypes: PaymentType[];
  paymentTypeId: string | null;
  onPaymentTypeChange: (id: string) => void;
  disabled: boolean;
}

/**
 * The enrol step's offer to open the learner's per-visit account (PD-0019): a box, the
 * Payment Type to bill with (hidden when there is only one), or why it cannot be ticked.
 */
const PerVisitContractField: React.FC<PerVisitContractFieldProps> = ({
  checked,
  onCheckedChange,
  alreadyActive,
  paymentTypes,
  paymentTypeId,
  onPaymentTypeChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const selectId = useId();
  const noPaymentType = paymentTypes.length === 0;
  const locked = alreadyActive || noPaymentType;

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-grey-30 dark:text-grey-80">
        <input
          type="checkbox"
          checked={checked && !locked}
          disabled={disabled || locked}
          onChange={(e) => onCheckedChange(e.target.checked)}
        />
        {t("training.enrolments.perVisit.label")}
      </label>
      {alreadyActive && (
        <p className="text-sm text-grey-50 dark:text-grey-70">{t("training.enrolments.perVisit.alreadyActive")}</p>
      )}
      {!alreadyActive && noPaymentType && (
        <Link
          to={`${ROUTES.BILLING}?tab=payment-types`}
          className="text-sm text-primary-40 dark:text-primary-90 hover:underline"
        >
          {t("training.enrolments.perVisit.noPaymentType")}
        </Link>
      )}
      {checked && !locked && paymentTypes.length > 1 && (
        <div className="space-y-1">
          <label htmlFor={selectId} className="block text-sm text-grey-30 dark:text-grey-80">
            {t("training.enrolments.perVisit.paymentType")}
          </label>
          <select
            id={selectId}
            className="input w-full"
            value={paymentTypeId ?? ""}
            onChange={(e) => onPaymentTypeChange(e.target.value)}
            disabled={disabled}
          >
            {paymentTypes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default PerVisitContractField;
